import React from "react";
import { Button } from "@mui/material";
import { useTranslate } from '@refinedev/core';
import { useAuth } from "../../contexts/auth-context";

interface CheckoutButtonProps {
  selectedType: 'individual' | 'pro' | 'pro_case';
  duration?: 'monthly' | 'annual';
  isAnnual?: boolean;
  people?: number;
  planName: string;
  popular?: boolean;
}

const CheckoutButton: React.FC<CheckoutButtonProps> = ({
  selectedType,
  duration = 'monthly',
  isAnnual = false,
  people = 1,
  planName,
  popular = false
}) => {
  const translate = useTranslate();
  const { user } = useAuth();
  
  const getStripePriceId = (): string => {
    if (selectedType === 'pro') {
      // Use the actual Stripe price IDs
      if (isAnnual) {
        return 'price_1RtQUBH3YB1pNNHqwiPXxX2E'; // pro yearly
      } else {
        return 'price_1RtQUqH3YB1pNNHqROWuGHwg'; // pro monthly
      }
    } else if (selectedType === 'pro_case') {
      // Use the actual Stripe price IDs
      if (isAnnual) {
        return 'price_1RtQufH3YB1pNNHqH3jHi4Rq'; // pro_case yearly
      } else {
        return 'price_1RtQuOH3YB1pNNHqyASoltsm'; // pro_case monthly
      }
    } else { // selectedType === 'individual'
      // Individual plan only has monthly pricing now
      return 'price_1RoMCeH3YB1pNNHqefteAxQO'; // individual, monthly - UPDATE WITH ACTUAL STRIPE PRICE ID
    }
  };

  const handleClick = async () => {
    const stripePriceId = getStripePriceId();
    
    // Log user info for debugging
    console.log('🔍 Current user info:', {
      id: user?.id,
      email: user?.email,
      role: user?.role,
    });
    
    console.log('Sending checkout request with:', {
      priceId: stripePriceId,
      selectedType,
      duration,
      isAnnual,
      people,
      planName,
      userEmail: user?.email
    });

    try {
      console.log('Making fetch request to:', "http://localhost:3002/create-checkout-session");
      const requestBody = {
        priceId: stripePriceId,
        selectedType,
        duration,
        isAnnual,
        people,
        planName,
        userEmail: user?.email || undefined
      };
      console.log('Request body:', JSON.stringify(requestBody, null, 2));

      const res = await fetch("http://localhost:3002/create-checkout-session", {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log('Response status:', res.status);
      console.log('Response headers:', Object.fromEntries(res.headers.entries()));

      if (!res.ok) {
        const errorText = await res.text();
        console.error('API Error:', errorText);
        throw new Error(`API Error: ${res.status} - ${errorText}`);
      }

      const data = await res.json();
      console.log('API Response:', data);

      if (!data.url) {
        console.error('No URL in response:', data);
        throw new Error('No checkout URL received from server');
      }

      console.log('Redirecting to:', data.url);
      window.location.href = data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : 'Unknown'
      });
      alert(translate('pricing.errors.paymentSessionError'));
    }
  };

  return (
    <Button
      onClick={handleClick}
      variant={popular ? "contained" : "outlined"}
      fullWidth
      size="large"
      sx={{
        mt: 2,
        ...(popular && {
          backgroundColor: 'primary.main',
          '&:hover': {
            backgroundColor: 'primary.dark',
          }
        })
      }}
    >
      {translate('pricing.startFreeTrial')}
    </Button>
  );
};

export default CheckoutButton;
